/**
 * Distillation lab generator grid.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { cmatEigenvalues, toTuples } from "@csk/core/linear";
import {
  expOf,
  isImmune,
  jacobiResidual,
  structuralAxiomsHold,
  type AuthoritySubgroup,
  type Complex,
  type ComplexMatrix,
  type Generator,
} from "../../algorithms/distillation.js";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
export interface DistillationGeneratorGridProps {
  readonly generators: readonly Generator[];
  readonly current: readonly Generator[];
  readonly pending: Generator | null;
  readonly subgroup: AuthoritySubgroup;
  readonly surface: number;
}
function formatComplex(z: Complex): string {
  const round = (x: number): number => Math.round(x * 1000) / 1000;
  const re = round(z[0]);
  const im = round(z[1]);
  if (Math.abs(im) < 1e-9) return String(re);
  if (Math.abs(re) < 1e-9) return `${String(im)}i`;
  return `${String(re)}${im >= 0 ? "+" : "−"}${String(Math.abs(im))}i`;
}
function formatMatrix(matrix: ComplexMatrix): string {
  const rows = toTuples(matrix).map(
    (row) => `[${row.map((z) => formatComplex(z)).join(", ")}]`,
  );
  return `[${rows.join(", ")}]`;
}
export function DistillationGeneratorGrid({
  generators,
  current,
  pending,
  subgroup,
  surface,
}: DistillationGeneratorGridProps): React.JSX.Element {
  return (
    <>
      <PanelSurface.SectionHeading>
        Generators ({String(surface)} surviving) — live on the @csk/core GL(n,ℂ)
        realization
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-2.5">
          {generators.map((generator) => (
            <GeneratorCard
              key={generator.id}
              generator={generator}
              present={current.some(
                (candidate) => candidate.id === generator.id,
              )}
              pending={pending}
              subgroup={subgroup}
            />
          ))}
        </div>
      </PanelSurface.Card>
    </>
  );
}
interface GeneratorCardProps {
  readonly generator: Generator;
  readonly present: boolean;
  readonly pending: Generator | null;
  readonly subgroup: AuthoritySubgroup;
}
function GeneratorCard({
  generator,
  present,
  pending,
  subgroup,
}: GeneratorCardProps): React.JSX.Element {
  const immune = isImmune(generator, subgroup);
  const expX = expOf(generator);
  const eigenvalues = cmatEigenvalues(generator.matrix);
  const structural = structuralAxiomsHold(generator);
  const residual = jacobiResidual(generator);
  const willStripNext =
    present && pending !== null && pending.id === generator.id;
  const accent = immune ? panelTokens.immune : panelTokens.violation;
  return (
    <PanelSurface.TintedRow
      color={present ? accent : panelTokens.muted}
      muted={!present}
      className="transition-all"
      leading={
        <span
          aria-hidden
          className="inline-flex h-7 w-7 items-center justify-center rounded-[8px] font-mono text-[12px] font-extrabold"
          style={{
            background: `${accent}22`,
            color: accent,
          }}
        >
          {generator.label.slice(0, 2)}
        </span>
      }
      heading={generator.label}
      trailing={
        !present
          ? PanelSurface.badge("stripped", panelTokens.muted)
          : immune
            ? PanelSurface.badge("immune", panelTokens.immune)
            : willStripNext
              ? PanelSurface.badge("stripping next", panelTokens.accentGold)
              : PanelSurface.badge("A1 violator", panelTokens.violation)
      }
    >
      <div className="mb-2 font-mono text-[11px] leading-[1.5] text-[var(--vid-muted)]">
        {generator.description}
      </div>

      <div className="flex flex-col gap-0.5 font-mono text-[10.5px] leading-[1.5] text-[var(--vid-text)]">
        <div>
          <span className="text-[var(--vid-muted)]">X =</span>{" "}
          {formatMatrix(generator.matrix)}
        </div>
        <div>
          <span className="text-[var(--vid-muted)]">exp(X) =</span>{" "}
          {formatMatrix(expX)}
        </div>
        <div>
          <span className="text-[var(--vid-muted)]">eigvals =</span>{" "}
          {eigenvalues.map((z) => formatComplex(z)).join(", ")}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {PanelSurface.badge(
          `a1 ${immune ? "✓ exp(X) ∈ H" : "✗ exp(X) ∉ H"}`,
          accent,
          `A1 authority subgroup membership — exp(X) ${immune ? "∈" : "∉"} H`,
        )}
        {(["a2", "a3", "a4"] as const).map((axiom) => (
          <span key={axiom}>
            {PanelSurface.badge(
              `${axiom} ✓ built in`,
              panelTokens.accentTeal,
              axiomTitle(axiom, residual),
            )}
          </span>
        ))}
      </div>

      {!structural && present && (
        <div className="mt-2 text-[11px] leading-[1.5] text-[var(--vid-violation)]">
          structural axiom check failed — unexpected for gl(n,ℂ)
        </div>
      )}
      {!immune && present && (
        <div className="mt-2 text-[11px] leading-[1.5] text-[var(--vid-violation)]">
          exp(X) ∉ H — A1-violating composite structurally forbidden;
          distillation makes it unconstructable by construction.
        </div>
      )}
    </PanelSurface.TintedRow>
  );
}
function axiomTitle(axiom: "a2" | "a3" | "a4", residual: number): string {
  if (axiom === "a2") {
    return "A2 spectral separation — built in over GL(n,ℂ): n eigenvalues always exist";
  }
  if (axiom === "a3") {
    return "A3 temporal flow — the exp semigroup law holds for every gl(n,ℂ) generator";
  }
  return `A4 resource accounting — Jacobi residual |det(exp X) − exp(tr X)| = ${residual.toExponential(1)} ≈ 0`;
}
